import { useCallback, useMemo, useState, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { Banner, Link, SuccessIcon, TruncateText } from '@panwds/react-ui'
import { parseISO, format } from 'date-fns'
import DropDown from './common/DropDown/DropDown'
import {
  APP_ID_LOGGING_SERVICE,
  CSP_FIELD_NAME,
  CREATE_NEW,
  REGION_FIELD_NAME,
  TSG_FIELD_NAME,
} from './common/GlobalConstants'
import { LICENSE_EXPIRY_TITLE, LICENSE_QUANTITY_TITLE, CDL_MIGRATION_MESSAGE, CDL_MIGRATION_NO_INSTANCE, CDL_DEVICE_ASSOCIATIONS_MESSAGE, DEVICE_ASSOCIATIONS_URL, DEVICE_ASSOCIATIONS_TEXT, DEVICE_ASSOCIATIONS_LINK_FOLLOW_MESSAGE } from '../constants'
import KeyValueDisplay from './common/KeyValueDisplay'
import { ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP, ACTIVATION_UI_ERROR_CODES } from '../ErrorCodes'
import { appIdsEligibleForActivation, isNonemptyArray, isDasCdlEnabled } from './common/utils'
import SubscriptionService from '../services/SubscriptionService'
import { getVarValue } from '../../common/utils/Sparky'
import { FF_AIOPS_CDL_TSG_MIGRATION } from '../../common/utils/FeatureFlags'
import UAccordionHeader from './unified/UAccordionHeader'
import UTextBox from './unified/UTextBox'
import ScreenLoader from '../components/common/ScreenLoader'

import type { VFC } from 'react'
import type { AppManifest } from '@sparky/framework'
import type { CommonStoreState } from '../store'
import type { IDropDownData, IDropDownProps } from './common/DropDown/DropDown'
import type { ManifestFlags } from '../renderer'
import type { Actions } from '../actions'
import { updateActivationData, updateActivationInstances } from '../actions'
import type { BannerProps } from './unified/UAccordion'

const defaultProps = {
  title: 'Cortex Data Lake',
  subtitle: ''
}

const CDL_REGION_FIELD_NAME = `${APP_ID_LOGGING_SERVICE}-region`

type DefaultCDLFieldProps = {
  state: CommonStoreState
  actions: Actions
  cdlDropDownProps: IDropDownProps
  isUnifiedDisplay?: boolean
  title?: string
  subtitle?: string
  updateTitle?: (title: string) => void
  setBanner?: (props: BannerProps) => void
}

type LicenseDisplay = {
  tenant_id: string,
  purchased_size: any,
  purchased_unit: string,
  license_expiration: any,
}
type CSPInstance = {
  tenant_display_name: string,
  tenant_id: string,
  extra: any,
  purchased_unit: string,
  license_expiration: any,
  region: string
}
type CDLMigrationStateType = {
  fieldName: string;
  cspInstances: CSPInstance[],
  isCDLMigration: boolean
}

const DefaultCDLField: VFC<DefaultCDLFieldProps> = ({ state, isUnifiedDisplay, title, subtitle, actions, setBanner, ...props }) => {
  const { apps, licenses, instances, tsg, loadingTsg, manifest, globalTsgId } = state
  const { setValue, setError, clearErrors, watch, getValues, trigger } = useFormContext()
  const [cdlList, setCdlList] = useState<IDropDownData[]>([])
  const [cdlTenantId, setCdlTenantId] = useState('new')
  const [isCdlDisabled, setCdlDisabled] = useState(false)

  const [cdlMigrationState, setCdlMigrationState] = useState<CDLMigrationStateType>({
    fieldName: 'cdl',
    cspInstances: [],
    isCDLMigration: false
  })
  const [currentCdl, setCdl] = useState<LicenseDisplay>({
    tenant_id: '',
    purchased_size: 0,
    purchased_unit: '',
    license_expiration: '',
  })
  const [isCSPCdlLoading, setIsCSPCdlLoading] = useState<boolean>(false)
  const [allocatedSize, setAllocatedSize] = useState<string>('')

  const currentCSP = watch(CSP_FIELD_NAME)
  const currentPlatformRegion = watch(REGION_FIELD_NAME)

  const cdlManifest = useMemo<AppManifest | undefined>(() => {
    return apps?.find(each => each.app_id === APP_ID_LOGGING_SERVICE)
  }, [apps])

  const cdlInstance = useMemo(() => {
    return instances.get(APP_ID_LOGGING_SERVICE)
  }, [instances])
  const { cdlLicense, createNew } = useMemo(() => {
    const cdlLicense = licenses?.get(APP_ID_LOGGING_SERVICE)?.licenses[0]
    // need to set initial size correct
    setAllocatedSize(String(cdlLicense?.remained_size))
    const createNew = {
      value: CREATE_NEW,
      description: CREATE_NEW,
      children: CREATE_NEW,
      key: 'new',
      id: 'new',
      tenant_id: 'new',
      purchased_size: cdlLicense?.remained_size,
      disabled: false
    }
    return { cdlLicense, createNew }
  }, [licenses])

  const isCdlLicenseAvailable = cdlLicense && !cdlLicense.is_csp_activated

  const maxQuantity = useMemo(() => {
    const _logging_service = licenses?.find(_each => _each.app_id === APP_ID_LOGGING_SERVICE)
    const _remained_size = _logging_service ? _logging_service?.licenses?.find(_each => _each.app_id === APP_ID_LOGGING_SERVICE)?.remained_size ?? 0 : 0
    if (cdlTenantId === 'new') {
      return _remained_size
    }
    const _cdlInstance = instances.find(e => e.app_id === APP_ID_LOGGING_SERVICE && cdlTenantId === e.tenant_id)
    if (_cdlInstance) {
      return (cdlList?.find((_each) => _each.tenant_id === cdlTenantId)?.purchased_size ?? 0) + _remained_size
    }
    return cdlList?.find((_each) => _each.tenant_id === cdlTenantId)?.purchased_size
  }, [cdlList, cdlTenantId, instances, licenses])

  const cdlQuantityValidator = useCallback((value) => {
    const _cdlInstance = instances.find(e => e.app_id === APP_ID_LOGGING_SERVICE)
    const minQuantity = _cdlInstance ? _cdlInstance?.extra?.purchased_size ?? 1 : 1
    if (isNonemptyArray(cdlList)) {
      if (typeof setBanner === 'function') {
        setBanner({ display: (+value < minQuantity || +value > maxQuantity), message: 'Cdl Quantity Validation Error', appearance: 'error' })
      }
      return !(+value < minQuantity || +value > maxQuantity)
    }

    typeof setBanner === 'function' && setBanner({ display: false, message: '', appearance: 'error' })
    return true
  }, [cdlList, instances, maxQuantity, setBanner])

  useEffect(() => {
    const regionDetails = getValues('region')
    setValue(CDL_REGION_FIELD_NAME, regionDetails?.value ?? '')
    if (cdlMigrationState.cspInstances?.length > 0) {
      const _instances = cdlMigrationState.cspInstances.length > 0 ? cdlMigrationState.cspInstances.map((each: { tenant_display_name: string; tenant_id: string; region: string; license_expiration: string; extra: any }) => ({
        value: `${each.tenant_display_name}`,
        children: `${each.tenant_display_name}`,
        description: 'existing instance',
        key: each.tenant_id,
        id: each.tenant_id,
        tenant_id: each.tenant_id,
        purchased_size: each?.extra?.purchased_size,
        disabled: currentPlatformRegion?.id ? each.region !== currentPlatformRegion?.id : false
      })) : []
      if (isCdlLicenseAvailable) {
        _instances.push(createNew)
      }
      setCdlList(_instances)
      //setValue('migrate_csp_instance', null)
    }
    /**
     * we do not want run this effect on 'cdlMigrationState', however it is required when region changes, thus disabling dep check.
     * please do not remove next line
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlatformRegion, getValues, setValue, cdlLicense])

  const updateCdlSize = useCallback((allocated_size) => {
    let allocation_type: 'FULL' | 'SHARED' = 'FULL'
    if (cdlLicense?.allocated_size === 0) {
      // license not been used
      allocation_type = +allocated_size === +cdlLicense.purchased_size ? 'FULL' : 'SHARED'
    }
    else {
      // license already been used
      allocation_type = 'SHARED'
    }
    updateActivationInstances({
      [APP_ID_LOGGING_SERVICE]: {
        app_id: APP_ID_LOGGING_SERVICE,
        activation_type: cdlTenantId === 'new' || cdlTenantId.length === 0 ? 'NEW' : 'AMEND',
        license_id: licenses?.get(APP_ID_LOGGING_SERVICE)?.license_id,
        allocation_type,
        allocation: [
          {
            app_id: APP_ID_LOGGING_SERVICE,
            license_type: cdlLicense?.license_type ?? cdlList?.find(_each => _each.tenant_id === cdlTenantId)?.license_type ?? '',
            tenant_id: cdlTenantId,
            allocation_size: +allocated_size
          }
        ]
      }
    })
  }, [cdlLicense?.allocated_size, cdlLicense?.license_type, cdlLicense?.purchased_size, cdlList, cdlTenantId, licenses])

  useEffect(() => {
    updateCdlSize(allocatedSize)
  }, [allocatedSize, updateCdlSize])



  const appIdsToBeActivated = useMemo(() => {
    return appIdsEligibleForActivation(licenses, manifest?.primary_app_id as string, manifest?.flags as ManifestFlags)
  }, [licenses, manifest])

  const isCDLMigrationEnabled = getVarValue(FF_AIOPS_CDL_TSG_MIGRATION, null) && (manifest?.flags?.migrate_cdl === undefined || manifest?.flags?.migrate_cdl)
  const resetCDLMigrationState = useCallback(() => {
    setCdlMigrationState({
      cspInstances: [],
      fieldName: 'cdl',
      isCDLMigration: false
    })
  }, [])
  const setCDLMigrationState = useCallback((instanceData = []) => {
    setCdlMigrationState({
      cspInstances: instanceData,
      fieldName: 'migrate_csp_instance',
      isCDLMigration: true
    })
  }, [])

  useEffect(() => {
    if (!loadingTsg) {
      clearErrors('cdl')
      clearErrors('cdl-virtual')
      if (tsg?.id && !cdlInstance && manifest?.flags?.cdl_required && (!cdlLicense || cdlLicense?.is_csp_activated)) {
        setCdl({
          tenant_id: 'NA',
          purchased_size: 'NA',
          purchased_unit: '',
          license_expiration: 'NA',
        })
        setError('cdl', { message: ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP[ACTIVATION_UI_ERROR_CODES.NO_ACCESS_TO_CDL] })
        setError('cdl-virtual', { message: ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP[ACTIVATION_UI_ERROR_CODES.NO_ACCESS_TO_CDL] })
        //setValue('migrate_csp_instance', null)
      }
      else if (cdlInstance) {
        setCdl({
          tenant_id: cdlInstance.tenant_id,
          purchased_size: cdlInstance.extra?.purchased_size,
          purchased_unit: 'TB',
          license_expiration: cdlInstance.license_expiration,
        })
        if (appIdsToBeActivated?.length === 1 && appIdsToBeActivated.includes(APP_ID_LOGGING_SERVICE)) {
          /**
           * Since react hook form clears all the errors which are manually set on every validation cycle.
           * setError does not persist error by design, error should always align and against validation rules. the method setError was made for set error manually in the handleSubmit.
           * if we make setError persist, then user will have to manually remove error during submit, which is not ideal.
           *
           * there are two option to handle
           * 1 - have the validation rule in component registration , so that each form trigger, validation rule runs and keeps the error if it needs
           * 2 - the hack, use a dummy field (not registered) name to set the error, so it doesn't clear out on for trigger. since the dummy field is not registered with the form, it doesn't know to clear it
           * for this use case, we are going with option 2, because we have other business rule in order to determine whether field is valid or not.
           */
          setError('cdl', { message: `${cdlManifest?.display_name} instance already exists in the tenant.` })
          setError('cdl-virtual', { message: `${cdlManifest?.display_name} instance already exists in the tenant.` })

          // setValue('migrate_csp_instance', null)
        }
        else {
          // setValue('cdl', {
          //   value: `${cdlInstance?.tenant_display_name || cdlInstance?.tenant_instance_name || cdlInstance?.serial_number} - ${cdlInstance?.extra?.purchased_size} TB`,
          //   description: 'existing instance',
          //   key: cdlInstance?.tenant_id,
          //   id: cdlInstance?.tenant_id,
          // })
          // setValue('migrate_csp_instance', null)
        }
      }
      else if (cdlLicense) {
        setCdl({
          tenant_id: '', // don't set it to new, default it to empty
          purchased_size: cdlLicense?.remained_size ?? 0,
          purchased_unit: cdlLicense.purchased_unit,
          license_expiration: cdlLicense.license_expiration,
        })
        if (isCdlLicenseAvailable) {
          setCdlList([createNew])
          // setValue('cdlPurchasedSize', '')
          // setValue('migrate_csp_instance', null)
        }
      }
    }
  }, [cdlInstance, cdlLicense, clearErrors, loadingTsg, setError, setValue, tsg, appIdsToBeActivated, manifest?.flags?.cdl_required, isCdlLicenseAvailable, createNew, cdlManifest])

  const getCDLinstances = useCallback(async (_cspId) => {
    let _instances = []
    /*
    * not passing the 'currentTsg' as deps because we dont want to run this routine when tsg changes, that is already taken care by the instnace change.
    * so if tsg is not selected,event create new, we are not making logging_service call.
    * as soon as tsg change happens, we are restting csp dropdown value.
    * so this we we dont depend on ela flag or any other flag.
    */
    const currentTsg = getValues(TSG_FIELD_NAME)
    if (isCDLMigrationEnabled && currentTsg?.id !== '' && _cspId && !isCSPCdlLoading) {
      try {
        setIsCSPCdlLoading(true)
        const region = getValues(REGION_FIELD_NAME) || null
        const { data } = await SubscriptionService.instancesFromCSP.get(APP_ID_LOGGING_SERVICE, globalTsgId, _cspId)
        _instances = data?.length > 0 ? data.map((each: { tenant_display_name: string; tenant_id: string; region: string; license_expiration: string; extra: any }) => ({
          value: `${each.tenant_display_name}`,
          children: `${each.tenant_display_name}`,
          description: 'existing instance',
          key: each.tenant_id,
          id: each.tenant_id,
          tenant_id: each.tenant_id,
          purchased_size: each?.extra?.purchased_size,
          disabled: region?.id ? each.region !== region?.id : false
        })) : []
        setCDLMigrationState(data)
        if (isCdlLicenseAvailable) {
          _instances.push(createNew)
        }
        setCdlList(_instances)
        setCdlTenantId('new')
        setIsCSPCdlLoading(false)
      }
      catch (error) {
        if (isCdlLicenseAvailable) {
          _instances.push(createNew)
        }
        setCdlList(_instances)
        setIsCSPCdlLoading(false)
        resetCDLMigrationState()
      }
    }
    else if (isCdlLicenseAvailable) {
      _instances.push(createNew)
      setCdlList(_instances)
      setCdlTenantId('new')
      resetCDLMigrationState()
    }
    else {
      setCdlList([])
      resetCDLMigrationState()
      // setValue('migrate_csp_instance', null)
      // setValue('cdl', null)
    }
    if (manifest?.flags?.cdl_required) {
      // setValue('cdl', null) // for required cdl apps
      // setValue('migrate_csp_instance', null)
    }
  }, [getValues, isCDLMigrationEnabled, isCSPCdlLoading, isCdlLicenseAvailable, manifest?.flags?.cdl_required, globalTsgId, setCDLMigrationState, createNew, resetCDLMigrationState])
  /**
   * While instance call is going, do not perform any operation
   * Once Instance call is done, check if there is any CDL instance
   * if CDL instance exist, set the CDL dropdown value
   * if CDL instance does not exist and cdl migration is enabled make csp call to get the cdl instance.
   * if csp cdl instances doesnt exist, check if magic link has cdl license than show "Create New" option
   */
  useEffect(() => {
    if (!loadingTsg) {
      const _cdlInstance = instances.find(e => e.app_id === APP_ID_LOGGING_SERVICE)
      if (_cdlInstance) {
        const res = {
          value: `${_cdlInstance.tenant_display_name || _cdlInstance.tenant_instance_name || _cdlInstance.serial_number} - ${_cdlInstance.extra?.purchased_size} TB`,
          description: 'existing instance',
          children: `${_cdlInstance.tenant_display_name || _cdlInstance.tenant_instance_name || _cdlInstance.serial_number} - ${_cdlInstance.extra?.purchased_size} TB`,
          key: _cdlInstance.tenant_id,
          id: _cdlInstance.tenant_id,
          purchased_size: _cdlInstance?.extra?.purchased_size,
          tenant_id: _cdlInstance?.tenant_id
        }
        setCdlList([res])
        setCdlTenantId(res?.tenant_id)
        setAllocatedSize(res?.purchased_size)
        //trigger('cdlPurchasedSize')
      }
      else {
        getCDLinstances(currentCSP?.id)
      }
    }
    else {
      resetCDLMigrationState()
    }
    /**
     * getCDLinstances is fn will be same on each re-render, but it points to a different location in memory every time, so it would fail the equality check and cause an infinite re-render loop.
     * please do not remove next line
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingTsg, instances, currentCSP])

  useEffect(() => {
    if (!loadingTsg) {
      const _cdlInstance = instances.find(e => e.app_id === APP_ID_LOGGING_SERVICE)
      if (_cdlInstance) {
        const defaultValue = cdlList?.find(_each => _each.key === cdlTenantId)
        setValue(cdlMigrationState.fieldName, defaultValue)
        setValue('cdlPurchasedSize', _cdlInstance?.extra?.purchased_size ?? 0)
        setCdlDisabled(true)
      }
      else {
        setCdlDisabled(false)
        const defaultValue = cdlList?.find(_each => _each.key === cdlTenantId)
        setValue(cdlMigrationState.fieldName, defaultValue)
        setValue('cdlPurchasedSize', defaultValue?.purchased_size)
      }
      trigger('cdlPurchasedSize')
    }
  }, [cdlList, cdlMigrationState.fieldName, cdlTenantId, instances, loadingTsg, setValue, trigger])


  const onCdlChange = useCallback((value: IDropDownData) => {
    const migrationInstance: any = []
    if (isCDLMigrationEnabled && cdlMigrationState.isCDLMigration) {
      if (value.value !== CREATE_NEW) {
        const cspInstance = cdlMigrationState.cspInstances?.find((i) => i.tenant_id === value.id)
        cspInstance && setCdl({
          tenant_id: cspInstance?.tenant_id,
          purchased_size: cspInstance?.extra?.purchased_size,
          purchased_unit: 'TB',
          license_expiration: cspInstance?.license_expiration,
        })
        migrationInstance.push(cspInstance?.tenant_id)
      }
    }
    setAllocatedSize(value?.purchased_size ?? '0')
    setCdlTenantId(value?.id ?? '')
    if (isNonemptyArray(migrationInstance)) {
      updateActivationData({
        'migrate_csp_instance': {
          'tenant_ids': migrationInstance
        }
      })
    }
    //trigger('cdlPurchasedSize')
  }, [isCDLMigrationEnabled, cdlMigrationState.isCDLMigration, cdlMigrationState.cspInstances])

  const formatCdlDropDownValue = useCallback((item: any) => {
    return (
      <div
        className='tw-flex tw-flex-col'>
        <TruncateText>{item.value ?? item.key}</TruncateText>
      </div>
    )
  }, [])

  const Activated = () => (
    <div>
      <SuccessIcon size='xs' />
      <span className='tw-ml-2'>Activated</span>
    </div>
  )

  const showActivatedComponent = useMemo(() => cdlLicense?.is_csp_activated, [cdlLicense])
  const dropdown = useMemo(() => {
    return <DropDown
      {...{
        ...props.cdlDropDownProps,
        disabled: isCdlDisabled,
        dropDownList: cdlList,
        isLoading: loadingTsg || isCSPCdlLoading,
        callbacks: {
          changeHandlerCallback: onCdlChange,
          formatOptionsCallback: formatCdlDropDownValue
        },
        fieldName: cdlMigrationState.fieldName
      }} />
  }, [cdlList, cdlMigrationState.fieldName, formatCdlDropDownValue, isCSPCdlLoading, isCdlDisabled, loadingTsg, onCdlChange, props.cdlDropDownProps])

  const displayDetails = () => {
    const selectedCDL = getValues(cdlMigrationState.fieldName)
    const details = {
      title: cdlManifest?.display_name || 'Cortex Data Lake',
      data: [
        {
          label: LICENSE_QUANTITY_TITLE,
          value: `${currentCdl?.purchased_size} ${currentCdl?.purchased_unit}`
        },
        {
          label: LICENSE_EXPIRY_TITLE,
          value: currentCdl.license_expiration && currentCdl.license_expiration !== 'NA' ? format(parseISO(currentCdl?.license_expiration), 'MM/dd/yyyy') : 'NA'
        },
        ...showActivatedComponent ? [{
          label: 'activate',
          value: <Activated />,
          hideLabel: true
        }] : [],
        {
          label: 'CDL dropdown',
          value: dropdown,
          hideLabel: true
        },
        ...cdlMigrationState.isCDLMigration && cdlMigrationState.cspInstances?.length > 0 && selectedCDL?.id !== 'new' ? [{
          label: 'CDL Migration',
          value: CDL_MIGRATION_MESSAGE
        }] : [],
        ...cdlMigrationState.isCDLMigration && cdlMigrationState.cspInstances?.length === 0 && selectedCDL?.id !== 'new' ? [{
          label: 'CDL Migration',
          value: CDL_MIGRATION_NO_INSTANCE
        }] : []
      ],
    }
    return details
  }
  const updateCdlQuantity = useCallback((value) => {
    setAllocatedSize(value)
    updateCdlSize(value)
  }, [updateCdlSize])
  const cdlQuantityComponent = useMemo(() => {
    return <UTextBox {...{
      value: allocatedSize,
      onChangeHandler: updateCdlQuantity,
      placeholder: 'Enter CDL Quantity',
      name: 'cdlPurchasedSize',
      showLabel: false,
      label: 'Data Log Storage',
      subTitle: `Up to ${maxQuantity ?? 0} ${cdlLicense?.purchased_unit ?? 'TB'} available`,
      errorMessage: 'CDL_QUANTITY_INPUT_ERROR',
      validation: cdlQuantityValidator,
      link: {
        url: 'https://apps.paloaltonetworks.com/logging-service-calculator',
        text: 'Data log storage estimator',
        external: true,
      },
      height: '32px',
      width: '250px'
    }} />
  }, [allocatedSize, cdlLicense?.purchased_unit, cdlQuantityValidator, maxQuantity, updateCdlQuantity])

  const cdlRegionComponent = useMemo(() => {
    return <UTextBox {...{
      placeholder: 'CDL Region',
      name: CDL_REGION_FIELD_NAME,
      showLabel: false,
      label: 'CDL Region',
      subTitle: 'This is decided by your region selection',
      disabled: true,
      height: '32px',
      width: '250px'
    }} />
  }, [])

  const cdlDeviceAssociationsBanner = useMemo(() => {
    const linkMessage = <>Go to <Link href={DEVICE_ASSOCIATIONS_URL} target='_blank' rel="noreferrer">{DEVICE_ASSOCIATIONS_TEXT}</Link> {DEVICE_ASSOCIATIONS_LINK_FOLLOW_MESSAGE}</>
    const context = <span style={{ fontSize: '12px' }}>{CDL_DEVICE_ASSOCIATIONS_MESSAGE} {linkMessage} </span>
    return (
      <div className='cm-flex cm-flex-row' style={{ marginTop: '20px' }}>
        <div className='cm-flex cm-flex-col cm-pt-1 cm-flex-1'>
          <Banner showIcon> {context} </Banner>
        </div>
      </div>
    )
  }, [])

  return (
    <>
      {isCSPCdlLoading && <ScreenLoader />}
      {
        isUnifiedDisplay ?
          <div>
            <UAccordionHeader title={title || defaultProps.title} subtitle={subtitle || defaultProps.subtitle} />
            <div className='cm-flex cm-flex-row'>
              <div className='cm-flex-1'>
                {dropdown}
              </div>
              <div className='cm-flex cm-flex-col cm-pt-1  cm-flex-1'>
                {cdlQuantityComponent}
              </div>
              <div className='cm-flex cm-flex-col cm-pt-1 cm-flex-1'>
                {cdlRegionComponent}
              </div>
            </div>
            {isDasCdlEnabled(manifest?.flags) && cdlDeviceAssociationsBanner}
          </div> :
          <KeyValueDisplay {...displayDetails()} />
      }
    </>
  )

}

export default DefaultCDLField
